import { useEffect, useState } from "react";
import { RiseIn } from "@/components/site/Reveal";
import { IDS } from "@/lib/testIds";

const SUPPORT_EMAIL = "support@countersign.ca";

const FAQ = [
  {
    q: "How do I set up a safe word?",
    a: "Agree it in person, not over text or email. Pick something unguessable — two unrelated words work well. Then enter it in the app.",
  },
  {
    q: "What do I do if I think a call is fake?",
    a: "Hang up. Call the person back on the number already saved in your phone. Do not use a number the caller gives you.",
  },
  {
    q: "Someone asked for my safe word over text. Is that normal?",
    a: "No. A safe word is only ever spoken, and only ever to answer a challenge you made.",
  },
  {
    q: "How do I cancel my subscription?",
    a: (
      <>
        Through Apple, in Settings. See <a href="https://support.apple.com/en-ca/HT202039" target="_blank" rel="noreferrer noopener" className="inline-link" style={{ color: "var(--accent-ink)" }}>Apple's guide to managing subscriptions</a>.
      </>
    ),
  },
];

export default function Support() {
  useEffect(() => { document.title = "Support — Countersign"; }, []);

  return (
    <main id="main" data-testid={IDS.support.page}>
      <section style={{ padding: "clamp(56px, 7vw, 120px) 0 clamp(40px, 5vw, 80px)", position: "relative", overflow: "hidden" }}>
        <div className="signal-lamp" style={{ opacity: 0.5 }} />
        <div className="container-cs" style={{ position: "relative" }}>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(32px, 5vw, 80px)" }} className="sp-hero">
            <div>
              <RiseIn>
                <div className="chapter-tag" style={{ marginBottom: 22 }}><span className="dot" /> Support</div>
              </RiseIn>
              <RiseIn delay={0.05}>
                <h1 className="font-display" style={{ fontSize: "clamp(44px, 6.4vw, 88px)", margin: 0, lineHeight: 1.02 }}>
                  Write to a person. <em style={{ color: "var(--accent-ink)" }}>Not a ticket queue.</em>
                </h1>
              </RiseIn>
              <RiseIn delay={0.12}>
                <p className="lead" style={{ marginTop: 28, maxWidth: "42ch" }}>
                  If you or someone in your family is worried about a call, message, or subscription, we're here. Most emails get a reply within one working day.
                </p>
              </RiseIn>
            </div>

            <RiseIn delay={0.1}>
              <aside className="cs-card" style={{ padding: 28 }}>
                <div className="chapter-tag" style={{ marginBottom: 16 }}><span className="dot" /> Contact</div>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  data-testid={IDS.support.email}
                  className="font-display"
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "clamp(22px, 2.2vw, 30px)",
                    fontWeight: 600,
                    color: "var(--accent-ink)",
                    letterSpacing: "-0.01em",
                    display: "inline-block",
                    wordBreak: "keep-all",
                    overflowWrap: "normal",
                    lineHeight: 1.15,
                  }}
                >
                  {SUPPORT_EMAIL}
                </a>
                <p style={{ marginTop: 16, color: "var(--ink-secondary)", fontSize: 17 }}>
                  Tell us as much as you're comfortable sharing. Please don't include your safe word in an email. We never need it, and email isn't a place for a secret.
                </p>
              </aside>
            </RiseIn>
          </div>
          <style>{`
            @media (min-width: 960px) {
              .sp-hero { grid-template-columns: 1.15fr 0.85fr !important; align-items: end; }
            }
          `}</style>
        </div>
      </section>

      <section style={{ padding: "clamp(40px, 6vw, 100px) 0 clamp(64px, 8vw, 120px)", background: "var(--surface-secondary)", borderTop: "1px solid var(--separator)", borderBottom: "1px solid var(--separator)" }}>
        <div className="container-cs" style={{ maxWidth: 960 }}>
          <RiseIn>
            <div className="chapter-tag" style={{ marginBottom: 20 }}><span className="chapter-num" style={{ color: "var(--accent-ink)" }}>·</span> Frequently asked</div>
          </RiseIn>
          <RiseIn delay={0.05}>
            <h2 className="font-display" style={{ fontSize: "clamp(32px, 4.4vw, 60px)", margin: 0, maxWidth: "22ch" }}>
              The four questions we're asked most.
            </h2>
          </RiseIn>

          <div style={{ marginTop: 40 }} data-testid={IDS.support.faq}>
            {FAQ.map((item, i) => (
              <FAQItem key={i} n={String(i + 1).padStart(2, "0")} q={item.q} a={item.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: "clamp(48px, 6vw, 96px) 0 clamp(64px, 8vw, 120px)" }}>
        <div className="container-cs" style={{ maxWidth: 960 }}>
          <RiseIn>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "clamp(24px, 3vw, 48px)" }} className="rt-row">
              <div>
                <h3 className="font-display" style={{ fontSize: "clamp(24px, 2.8vw, 34px)", margin: 0, maxWidth: "22ch" }}>Response times.</h3>
              </div>
              <div className="prose-cs">
                <p>We aim to reply within one working day (Monday to Friday, Toronto time). If your message is about an active scam, please still write, but the most important thing is to <strong>hang up and call the person back on a number you already had.</strong></p>
                <p>If you're in immediate danger, call your local emergency number.</p>
              </div>
            </div>
            <style>{`
              @media (min-width: 720px) {
                .rt-row { grid-template-columns: 260px 1fr !important; align-items: start; }
              }
            `}</style>
          </RiseIn>
        </div>
      </section>
    </main>
  );
}

function FAQItem({ n, q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = `faq-${n}`;
  return (
    <div style={{ borderTop: "1px solid var(--separator)" }}>
      <button
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((v) => !v)}
        style={{
          width: "100%",
          minHeight: 72,
          display: "grid",
          gridTemplateColumns: "56px 1fr auto",
          alignItems: "center",
          gap: 24,
          padding: "22px 0",
          background: "transparent",
          border: 0,
          textAlign: "left",
          cursor: "pointer",
          color: "var(--ink-primary)",
        }}
      >
        <span className="font-display" style={{ fontFamily: "var(--font-display)", fontStyle: "italic", color: "var(--accent-ink)", fontSize: 22 }}>{n}</span>
        <span className="font-display" style={{ fontFamily: "var(--font-display)", fontSize: "clamp(20px, 2.2vw, 26px)", fontWeight: 500, letterSpacing: "-0.01em" }}>
          {q}
        </span>
        <span
          aria-hidden="true"
          style={{
            width: 44, height: 44, borderRadius: 999,
            border: "1px solid var(--separator)", display: "grid", placeItems: "center",
            fontFamily: "var(--font-display)", color: "var(--ink-primary)", fontSize: 22,
            transition: "transform 240ms cubic-bezier(.2,.7,.2,1), background 240ms",
            transform: open ? "rotate(45deg)" : "rotate(0)",
            background: open ? "var(--surface-secondary)" : "transparent",
          }}
        >+</span>
      </button>
      <div
        id={id}
        role="region"
        style={{
          display: "grid",
          gridTemplateRows: open ? "1fr" : "0fr",
          transition: "grid-template-rows 320ms cubic-bezier(.2,.7,.2,1)",
        }}
      >
        <div style={{ overflow: "hidden" }}>
          <div className="prose-cs" style={{ padding: "0 0 26px 80px" }}>
            <p>{a}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
